import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deactivateProduct, productsQueryKeys } from '@api/products';
import { ApiError } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { ToastLinkDescription } from '@/components/ui/toast';
import { useTranslation } from '../useTranslation';

export interface DeactivateProductInput {
  id: number;
  name: string;
}

export function useDeactivateProduct() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: DeactivateProductInput) => deactivateProduct(id),
    onSuccess: (data) => {
      const product = data.product;
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.detail(product.id) });

      const { dismiss } = toast({
        description: (
          <ToastLinkDescription
            name={product.name}
            href={`/products/${product.id}/edit`}
            label={t('deactivate.toast.success')}
            onNavigate={() => dismiss()}
          />
        ),
      });
    },
    onError: (error, variables) => {
      if (error instanceof ApiError && error.status === 422) {
        const { dismiss } = toast({
          description: (
            <ToastLinkDescription
              name={variables.name}
              href={`/products/${variables.id}/edit`}
              label={t('deactivate.toast.error_422')}
              onNavigate={() => dismiss()}
            />
          ),
          variant: 'destructive',
        });
        return;
      }

      const description = error instanceof ApiError && error.status < 500
        ? error.message
        : t('deactivate.toast.error_generic');

      toast({
        title: t('deactivate.toast.error'),
        description,
        variant: 'destructive',
      });
    },
  });
}
